import React, {useEffect, useMemo} from "react";
import {
    AdditiveBlending,
    Box3,
    GammaEncoding,
    LinearEncoding,
    MeshBasicMaterial,
    MeshLambertMaterial,
    Vector3
} from "three";
import {useRoomConfig} from "../../model/RoomConfigContext";
import {useScreenConfig} from "../../model/ScreenConfigContext";
import {ScreenType} from "../../config/ScreenConfig";
import useGLTF from "./hooks/useGLTF";
import {useTextures} from "./TextureContext";
import {useClonedTexture} from "./hooks/useClonedTexture";
import {useWallGeometry} from "./geometry/useWallGeometry";
import {useLedStripGeometry} from "./geometry/useLedStripGeometry";
import {useAudioConfig} from "../../model/AudioConfigContext";
import {useTimpanumGeometry} from "./geometry/useTimpanumGeometry";
import {useUnitPlaneGeometry} from "./geometry/useUnitPlaneGeometry";

function useWallTexture(color, aoMap, emissiveMap)
{

    const {transparentWalls} = useRoomConfig();
    const {builtInWall} = useAudioConfig();

    return useMemo(() => new MeshLambertMaterial({
        color,
        emissiveIntensity: 1.0,
        emissiveMap,
        emissive: 0xffffff,
        aoMap,
        transparent: transparentWalls === true && builtInWall === true? true: false,
        opacity: transparentWalls === true && builtInWall === true? 0.85: 1,
    }), [color, emissiveMap, aoMap, transparentWalls, builtInWall]);
}

export default function Room() {
    const ledStripHeight = 5;
    const ledStripOffset = 5;
    const {
        width,
        height,
        depth,
        floorColor,
        wallColor,
        ceilingColor,
        floorLedsOn,
        frontMargin,
        sideMargin,
        backMargin,
        roofHeight,
        transparentWalls,
    } = useRoomConfig();
    const {type} = useScreenConfig();
    const {builtInWall} = useAudioConfig();
    const cupboardModel = useGLTF("./assets/models/Meubel_LowTable001/Meubel_LowTable001.gltf", MeshLambertMaterial);
    const cupboardPosition = useMemo(() => {
        const pos = new Vector3();
        if (cupboardModel) {
            const bounds = new Box3();
            bounds.setFromObject(cupboardModel.scene);
            pos.z = -bounds.max.z - frontMargin;
        }
        return pos;
    }, [cupboardModel, frontMargin]);

    const {floorTexture, wallLEDTexture, wallAOTexture} = useTextures();
    const wallBackLEDTexture = useClonedTexture(wallLEDTexture);
    const wallSideLEDTexture = useClonedTexture(wallLEDTexture);
    const backMaterial = useWallTexture(wallColor, wallAOTexture, wallBackLEDTexture);

    const frontMaterial = useMemo(() => new MeshLambertMaterial({
        color: wallColor,
        aoMap: wallAOTexture,
        transparent: transparentWalls === true && builtInWall === true? true: false,
        opacity: transparentWalls === true && builtInWall === true? 0.85: 1,
    }), [wallColor, wallAOTexture, transparentWalls, builtInWall]);

    const fakeWallMaterial = useMemo(() => new MeshLambertMaterial({
        // color: wallColor,
        // aoMap: wallAOTexture,
        color: 0,
        transparent: true,
        opacity: 0.25,
        polygonOffset: true,
        polygonOffsetUnits: -5,
        polygonOffsetFactor: -2
    }), []);
    const sideMaterial = useWallTexture(wallColor, wallAOTexture, wallSideLEDTexture);
    const {podiumLEDTexture} = useTextures();

    const ledStripGeometry = useLedStripGeometry();
    const ledTexture = useClonedTexture(podiumLEDTexture);
    useEffect(() => {
        if (!ledTexture) return;
        ledTexture.repeat.y = .1;
        ledTexture.outputEncoding = LinearEncoding;
        ledTexture.encoding = GammaEncoding;
    }, [ledTexture]);

    const ledMaterial = useMemo(() => {
        return new MeshBasicMaterial({
            color: 0x0a0a80,
            map: ledTexture,
            blending: AdditiveBlending,
            polygonOffset: true,
            polygonOffsetUnits: -10,
            transparent: transparentWalls === true && builtInWall === true? true: false,
            opacity: transparentWalls === true && builtInWall === true? 0.85: 1,
        });
    }, [ledTexture, transparentWalls, builtInWall]);

    const floorMaterial = useMemo(() => new MeshLambertMaterial({
        map: floorTexture,
        color: floorColor,
        aoMap: wallAOTexture
    }), [floorTexture, floorColor, wallAOTexture]);
    const ceilingMaterial = useMemo(() => new MeshLambertMaterial({
        color: ceilingColor,
        aoMap: wallAOTexture
    }), [ceilingColor, wallAOTexture]);
    const wallGeometry = useWallGeometry();
    const unitPlaneGeometry = useUnitPlaneGeometry();
    const timpanumGeometry = useTimpanumGeometry();

    useEffect(() => {
            if (!floorTexture) return;
            floorTexture.repeat.set(width / 100, depth / 100);
        },
        [floorTexture, width, depth]);

    useEffect(() => {
        if (!wallSideLEDTexture) return;
        wallSideLEDTexture.repeat.set(depth / 100, 1);
    }, [wallSideLEDTexture, depth]);

    useEffect(() => {
        if (!wallBackLEDTexture) return;
        wallBackLEDTexture.repeat.set(width / 100, 1);
    }, [wallBackLEDTexture, width]);

    const roomWidth = builtInWall? width - sideMargin * 2: width;
    const roomDepth = builtInWall? depth - backMargin - frontMargin: depth;
    const offsetZ = builtInWall? -frontMargin : 0;

    const ceilingAngle = Math.atan2(roofHeight, width * .5);
    // pythagoras
    const roofWidth = Math.sqrt(width * .25 * width + roofHeight * roofHeight);



    return (
        <group>
            <group position={[0, 0, offsetZ]}>
                {/*front wall*/}
                <mesh scale={[roomWidth, height, 1]} rotation={[0, Math.PI, 0]}
                      material={frontMaterial} geometry={wallGeometry}/>
                {/*back wall*/}
                <mesh scale={[roomWidth, height, 1]} position={[0, 0, -roomDepth]}
                      material={backMaterial} geometry={wallGeometry}/>
                {/* left wall (use negative scale so tiling textures stays fixed to the front wall */}
                <mesh scale={[roomDepth, height, -1]} position={[roomWidth * .5, 0, -roomDepth * .5]}
                      rotation={[0, Math.PI * .5, 0]}
                      material={sideMaterial} geometry={wallGeometry}/>
                {/* right wall */}
                <mesh scale={[roomDepth, height, 1]} position={[-roomWidth * .5, 0, -roomDepth * .5]}
                      rotation={[0, Math.PI * .5, 0]}
                      material={sideMaterial} geometry={wallGeometry}/>

                {/* Floor leds */}
                <mesh visible={floorLedsOn} scale={[roomWidth, ledStripHeight, roomDepth]} geometry={ledStripGeometry}
                      position={[0, ledStripOffset, 0]}
                      material={ledMaterial}/>
                {
                    cupboardModel && type === ScreenType.UltraShortThrow ?
                        <primitive object={cupboardModel.scene} position={cupboardPosition}/> : <></>
                }
            </group>

            {/*floor*/}
            <mesh scale={[width, depth, 1]} position={[0, 0, -depth * .5]} rotation={[-Math.PI * .5, 0, 0]}
                  material={floorMaterial} geometry={unitPlaneGeometry}/>
            {/*ceiling*/}
            <mesh scale={[roofWidth, depth, 1]} position={[-width * .25, height + roofHeight * .5, -depth * .5]} rotation={[Math.PI * .5, ceilingAngle, 0]}
                  material={ceilingMaterial} geometry={unitPlaneGeometry} />
            <mesh scale={[roofWidth, depth, 1]} position={[width * .25, height + roofHeight * .5, -depth * .5]} rotation={[Math.PI * .5, -ceilingAngle, 0]}
                  material={ceilingMaterial} geometry={unitPlaneGeometry} />

            {/* roof */}
            <mesh geometry={timpanumGeometry} material={frontMaterial} scale={[width, roofHeight, 1]}
                  position={[0, height, 0]} rotation={[0, Math.PI, 0]}/>
            <mesh geometry={timpanumGeometry} material={frontMaterial} scale={[width, roofHeight, 1]}
                  position={[0, height, -depth]} rotation={[0, 0, 0]}/>

            {/* The real walls for built-in systems*/}
            <group visible={builtInWall}>
                {/*front wall*/}
                <mesh scale={[width, height, 1]} rotation={[0, Math.PI, 0]}
                      position={[0, 0, 0]}
                      material={fakeWallMaterial} geometry={wallGeometry}/>

                {/*back wall*/}
                <mesh scale={[width, height, 1]} position={[0, 0, -depth]}
                      material={fakeWallMaterial} geometry={wallGeometry}/>

                {/* left wall */}
                <mesh scale={[depth, height, 1]}
                      position={[width * .5, 0, -depth * .5]}
                      rotation={[0, -Math.PI * .5, 0]}
                      material={fakeWallMaterial} geometry={wallGeometry}/>
                {/* right wall */}
                <mesh scale={[depth, height, 1]}
                      position={[-width * .5, 0, -depth * .5]}
                      rotation={[0, Math.PI * .5, 0]}
                      material={fakeWallMaterial} geometry={wallGeometry}/>
            </group>
        </group>
    );
}
